import React, { Children, type FC, ReactElement, useRef, useState } from 'react';
import { styled } from '../../styles';
import { Embolden } from '../Embolden';
import { isCreatableOption } from '../Select/SelectElements/utils/guards';
import { Tooltip, shouldEnableTooltip } from '../Tooltip/Tooltip';
import { TagOption } from './utils/Tags.types';
import { TagOptionActionProps } from './TagOptionAction';
import { Typography } from '../Typography/Typography';
import { BpDesignSystem } from '../../types';
import { ListItemText } from '../List/ListItemText';

export const InnerMenuItem: FC<
  BpDesignSystem.Props<{
    option: TagOption;
    searchTerm?: string;
    isEditModeEnabled: boolean;
    optionActions?: ReactElement<TagOptionActionProps>[];
    optionProps: React.HTMLAttributes<HTMLElement> & {
      key: any;
    };
  }>
> = ({ option, dataTest, searchTerm, isEditModeEnabled, optionActions, optionProps, ...rest }) => {
  const [showTooltip, setTooltipState] = useState(false);
  const typographyRef = useRef<HTMLDivElement>(null);

  if (isCreatableOption(option)) {
    return (
      <ListItemText
        dataTest={`${dataTest}-list-item-create-value`}
        className="MuiAutocomplete-option"
        {...optionProps}
        {...rest}
      >
        <Embolden term={option.creatableTitle}>{option.creatableTitle}</Embolden>
      </ListItemText>
    );
  }

  const label = isEditModeEnabled ? (option.labelInEditMode ?? option.label) : option.label;

  return (
    <ListItemText
      dataTest={`${dataTest}-list-item`}
      data-test-label={option.label}
      data-value={option.value}
      style={{ height: 41, opacity: option.value ? 1 : 0.5 }}
      className="MuiAutocomplete-option"
      slotProps={{
        primary: {
          width: '100%'
        }
      }}
      {...optionProps}
      {...rest}
    >
      <StyledManageTags>
        <Typography
          style={isEditModeEnabled ? { opacity: 0.38 } : undefined}
          dataTest="overflow-typography"
          component="div"
          noWrap
          ref={typographyRef}
          onMouseEnter={() => setTooltipState(shouldEnableTooltip(typographyRef.current))}
        >
          <Tooltip title={label} dataTest="select-item-tooltip" placement="top" disabled={!showTooltip}>
            <Embolden term={searchTerm}>{label}</Embolden>
          </Tooltip>
        </Typography>
        {optionActions && isEditModeEnabled && (
          <div>
            {Children.map(optionActions, (Action, i) =>
              React.cloneElement(Action, {
                onClick: () => Action.props.onClick(option),
                key: `${Action.key ?? ''}${i}`
              })
            )}
          </div>
        )}
      </StyledManageTags>
    </ListItemText>
  );
};

const StyledManageTags = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const renderOption = ({
  option,
  dataTest,
  isEditModeEnabled,
  searchTerm,
  optionActions,
  optionProps
}: {
  option: TagOption;
  dataTest: string;
  isEditModeEnabled: boolean;
  searchTerm?: string;
  optionActions?: ReactElement<TagOptionActionProps>[];
  optionProps: React.HTMLAttributes<HTMLElement> & {
    key: any;
  };
}) => (
  <InnerMenuItem
    option={option}
    key={option.value}
    optionActions={optionActions}
    dataTest={dataTest}
    searchTerm={searchTerm}
    isEditModeEnabled={isEditModeEnabled}
    optionProps={optionProps}
  />
);
